import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import logger from "../utils/logger";

type DashboardItem = { id: string; name: string; workspaceId: string };

interface AuthContextType {
  token: string | null; // you can drop this if you want
  isAuthenticated: boolean;
  login: (district: string) => void;
  logout: () => Promise<void>;
  user: any | null;
  loading: boolean;
  dashboards: DashboardItem[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const api = axios.create({
  baseURL: process.env.REACT_APP_SAML_GATEWAY_URL || "http://localhost:9000",
  withCredentials: true,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [token, setToken] = useState<string | null>(null); // optional now
  const [user, setUser] = useState<any | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dashboards, setDashboards] = useState<DashboardItem[]>([]);

  const loadAuthState = useCallback(async () => {
    setLoading(true);
    try {
      // Just ask backend who we are; backend reads HttpOnly cookie
      const response = await api.get("/user");
      // console.log("Auth /user response:", response.data);
      setUser(response.data);
      setIsAuthenticated(true);
      setToken(null); // or setToken(response.data.token) if you ever return one
      const dashboardsData = await api.get("/listDashboards");
      setDashboards(dashboardsData.data as DashboardItem[]);
    } catch (error) {
      logger.error("Auth check failed:", error);
      setUser(null);
      setIsAuthenticated(false);
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = (district: string) => {
    const redirectUrl = encodeURIComponent(
      `${window.location.origin}/dashboard`
    );
    const backendUrl =
      process.env.REACT_APP_SAML_GATEWAY_URL || "http://localhost:9000";

    // Full-page navigation – crucial for SAML roundtrip
    window.location.href = `${backendUrl}/login?district=${district}&redirectUrl=${redirectUrl}`;
  };

  const logout = async () => {
    try {
      await api.post("/logout");
    } catch (error) {
      logger.error("Logout error:", error);
    }

    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    window.location.href = "/";
  };

  useEffect(() => {
    loadAuthState();
    // No need for history.replaceState based on token anymore
  }, [loadAuthState]);

  return (
    <AuthContext.Provider
      value={{
        dashboards,
        token,
        user,
        isAuthenticated,
        loading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

export { api };
